import { useState, useEffect } from 'react';
import { useTheme } from './theme';
import { db } from './dbClient';

export function InvestigationSources({ caseId, company }) {
  const C = useTheme();
  const [sources, setSources] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (caseId && company?.id) {
      loadSources();
    }
  }, [caseId]);

  async function loadSources() {
    try {
      setLoading(true);

      // Get sources directly from investigation_sources table
      const { data: sourcesData, error: sourcesErr } = await db
        .from('investigation_sources')
        .select('*')
        .eq('case_id', caseId)
        .order('confidence_score', { ascending: false });

      if (sourcesErr) throw sourcesErr;

      setSources(sourcesData || []);
      setSummary(sourcesData?.length ? { total_sources: sourcesData.length, avg_confidence: Math.round(sourcesData.reduce((sum, s) => sum + (s.confidence_score || 0), 0) / sourcesData.length) } : {});
    } catch (err) {
      console.error('Error loading investigation sources:', err);
    } finally {
      setLoading(false);
    }
  }

  const getRiskColor = (riskLevel) => {
    const colors = {
      critical: '#FF6B6B',
      high: '#FF9800',
      medium: '#FFA500',
      low: '#4CAF50',
    };
    return colors[riskLevel?.toLowerCase()] || C.textMid;
  };

  const getSourceIcon = (sourceType) => {
    const icons = {
      news: '📰',
      sanctions: '⚠️',
      public_record: '📋',
      blockchain: '⛓️',
      government: '🏛️',
      custom: '📌',
      adverse_media: '🔴',
      positive_news: '🟢',
    };
    return icons[sourceType] || '🔗';
  };

  if (loading) {
    return <div style={{ color: C.textDim, fontSize: '0.9rem' }}>Loading sources...</div>;
  }

  if (sources.length === 0) {
    return (
      <div style={{
        background: C.bg,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: '1.5rem',
        textAlign: 'center',
        color: C.textDim,
        fontSize: '0.9rem'
      }}>
        No investigation sources found for this case.
      </div>
    );
  }

  return (
    <div>
      {/* Summary cards */}
      {summary.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '0.75rem',
          marginBottom: '1.5rem'
        }}>
          {summary.map(s => (
            <div
              key={s.source_type}
              style={{
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                padding: '0.75rem',
                textAlign: 'center'
              }}
            >
              <div style={{ color: C.textDim, fontSize: '0.7rem', fontWeight: 600, marginBottom: '0.25rem', textTransform: 'uppercase' }}>
                {s.source_type}
              </div>
              <div style={{ color: C.text, fontWeight: 700, fontSize: '1.2rem', marginBottom: '0.25rem' }}>
                {s.count}
              </div>
              <div style={{ fontSize: '0.7rem', color: C.textDim }}>
                {s.adverse_count > 0 && <span>🔴 {s.adverse_count}</span>}
                {s.positive_count > 0 && <span style={{ marginLeft: s.adverse_count > 0 ? '0.5rem' : 0 }}>🟢 {s.positive_count}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sources list */}
      <div style={{ display: 'grid', gap: '0.75rem' }}>
        {sources.map(source => (
          <div
            key={source.id}
            style={{
              background: source.verified ? `${C.accent}08` : C.surface,
              border: `1px solid ${source.verified ? C.accent : C.border}`,
              borderRadius: 8,
              padding: '1rem',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = getRiskColor(source.risk_level)}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = source.verified ? C.accent : C.border}
          >
            {/* Header with source info */}
            <div
              onClick={() => setExpandedId(expandedId === source.id ? null : source.id)}
              style={{ cursor: 'pointer', marginBottom: '0.75rem' }}
            >
              <div style={{ display: 'flex', alignItems: 'start', gap: '0.75rem', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: '1.2rem' }}>{getSourceIcon(source.source_type)}</span>
                <div style={{ flex: 1 }}>
                  <div style={{
                    color: C.text,
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    {source.source_name || source.source_type}
                    {source.verified && (
                      <span style={{
                        background: C.accent,
                        color: '#0A0E1A',
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        padding: '0.2rem 0.4rem',
                        borderRadius: 3
                      }}>
                        ✓ VERIFIED
                      </span>
                    )}
                  </div>
                  {source.snippet_title && (
                    <div style={{ color: C.textMid, fontSize: '0.8rem', marginTop: '0.25rem' }}>
                      {source.snippet_title}
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {source.risk_level && (
                    <span style={{
                      background: getRiskColor(source.risk_level) + '22',
                      color: getRiskColor(source.risk_level),
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      padding: '0.2rem 0.5rem',
                      borderRadius: 3,
                      textTransform: 'uppercase'
                    }}>
                      {source.risk_level}
                    </span>
                  )}
                  <span style={{
                    background: source.is_positive ? '#4CAF5022' : '#FF6B6B22',
                    color: source.is_positive ? '#4CAF50' : '#FF6B6B',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    padding: '0.2rem 0.5rem',
                    borderRadius: 3
                  }}>
                    {source.is_positive ? '+ Positive' : '- Adverse'}
                  </span>
                </div>
              </div>

              {/* Date and confidence */}
              <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: C.textDim }}>
                <span>📅 {new Date(source.extracted_date).toLocaleDateString()}</span>
                <span>🎯 {source.confidence_score}% confidence</span>
              </div>
            </div>

            {/* Expanded content */}
            {expandedId === source.id && (
              <div style={{ marginTop: '1rem', borderTop: `1px solid ${C.border}`, paddingTop: '1rem' }}>
                {/* Snippet */}
                {source.snippet && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ color: C.textDim, fontSize: '0.7rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      EXCERPT
                    </div>
                    <div style={{
                      background: C.bg,
                      border: `1px solid ${C.border}`,
                      borderRadius: 4,
                      padding: '0.75rem',
                      fontSize: '0.85rem',
                      lineHeight: 1.5,
                      color: C.text,
                      fontStyle: 'italic'
                    }}>
                      {source.snippet}
                    </div>
                  </div>
                )}

                {/* Source URL - clickable */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <div style={{ color: C.textDim, fontSize: '0.7rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                    SOURCE LINK
                  </div>
                  <a
                    href={source.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: C.accent,
                      textDecoration: 'none',
                      fontSize: '0.85rem',
                      padding: '0.5rem 0.75rem',
                      background: `${C.accent}11`,
                      border: `1px solid ${C.accent}33`,
                      borderRadius: 4,
                      transition: 'all 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = `${C.accent}22`;
                      e.currentTarget.style.borderColor = `${C.accent}66`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = `${C.accent}11`;
                      e.currentTarget.style.borderColor = `${C.accent}33`;
                    }}
                  >
                    <span>🔗</span>
                    <span style={{ fontFamily: "'DM Mono', monospace", wordBreak: 'break-all' }}>
                      {source.source_url.substring(0, 60)}
                      {source.source_url.length > 60 ? '...' : ''}
                    </span>
                  </a>
                </div>

                {/* Entity info */}
                {source.entity_name && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ color: C.textDim, fontSize: '0.7rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      ENTITY
                    </div>
                    <div style={{ fontSize: '0.85rem', color: C.text, fontFamily: "'DM Mono', monospace" }}>
                      {source.entity_name}
                      {source.entity_type && <span style={{ color: C.textMid }}> • {source.entity_type}</span>}
                    </div>
                  </div>
                )}

                {/* Published date */}
                {source.published_date && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ color: C.textDim, fontSize: '0.7rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                      PUBLISHED
                    </div>
                    <div style={{ fontSize: '0.85rem', color: C.text }}>
                      {new Date(source.published_date).toLocaleDateString()}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default InvestigationSources;
